import { Map as MapIcon, Toc as TocIcon } from '@mui/icons-material';
import { ButtonGroup, Grid, Button as MButton, Theme } from '@mui/material';
import { createStyles, withStyles } from '@mui/styles';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { HeaderContent } from '../../../components/HeaderContent';
import * as routing from '../../../constants/Routing';
import { ClientsTableColumnKeys } from '../../../context/client/tableColumns';
import { TableColumnsSortConfig } from '../../../context/shared/tableColumns';
import { useRetrieveFlatClients } from '../../../hooks/client/useRetrieveFlatClients';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectResources } from '../../../hooks/project/useRetrieveActiveProjectResources';
import { useRetrieveRightPanelInfo } from '../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useRetrieveSupportDocs } from '../../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import eventsPanel, { PageViewPages } from '../../../services/events';
import ClientsCreateMenu from './ClientsCreateMenu';
import { ProjectAssetsClientsList } from './client-list';
import { ProjectAssetsClientsMap } from './client-map';
import { ClientRightPanel } from './right-panel/ClientRightPanel';

const styles = ({ breakpoints }: Theme) =>
  createStyles({
    divInside: {
      height: `100%`,
      width: `100%`,
      zIndex: 2,
      backgroundColor: `white`,
      display: `flex`,
      flexDirection: `row`,
    },
    divContainer: {
      height: `100%`,
      width: `100%`,
    },
    divContainerData: {
      width: `100%`,
      height: `100%`,
      [breakpoints.down(`sm`)]: {
        width: `100%`,
      },
    },
  });

interface ProjectSettingsClientsProps {
  classes: {
    divInside: string;
    divContainer: string;
    divContainerData: string;
  };
}

export const ProjectSettingsClients = withStyles(styles)(({ classes }: ProjectSettingsClientsProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { retrieveDoc } = useRetrieveSupportDocs();
  const { data: projectResources } = useRetrieveActiveProjectResources();
  const { data: user } = useRetrieveUser();
  const { data: activeProject } = useRetrieveActiveProject();

  const { detailType } = useRetrieveRightPanelInfo();

  const [sortClientsInfo, setSortClientsInfo] = useState<TableColumnsSortConfig<ClientsTableColumnKeys>>({
    orderBy: `created_at`,
    order: `desc`,
  });

  const [searchParameters, setSearchParameters] = useState<{ limit: number; offset: number }>({
    offset: 0,
    limit: 100,
  });

  const { data: flatClients, isLoading } = useRetrieveFlatClients();

  useEffect(() => {
    eventsPanel.pageView(PageViewPages.clients);
  }, []);

  /**
   * Check against the quota if new clients can be added
   */
  const canAdd = () => {
    if (!activeProject || !projectResources || !flatClients || isLoading) {
      return false;
    }
    return flatClients.length < projectResources.max_clients;
  };

  const isMapView = () =>
    location.pathname === routing.PROJECT_SETTINGS.CLIENTS.MAP.replace(`:projectId`, activeProject?.id ?? ``);

  return (
    <div className={classes.divInside}>
      <Grid container className={classes.divContainer} wrap="nowrap">
        <Grid container className={classes.divContainerData} direction="column" wrap="nowrap">
          <Grid container direction="column" style={{ padding: `20px`, paddingBottom: 0 }} wrap="nowrap">
            <Grid container direction="row" wrap="nowrap">
              <HeaderContent
                titleIntlId="clients.title"
                article={{
                  intlId: `clients.article`,
                  link: retrieveDoc({ supportPage: `clients`, language: user?.language.value }),
                }}
              />

              <Grid container item justifyContent="flex-end">
                {activeProject ? (
                  <ClientsCreateMenu
                    id="list-clients-create-actions"
                    intlId="clients.new"
                    disabled={!canAdd()}
                    style={{
                      backgroundColor: RoutalPalette.secondary40,
                      justifyContent: `space-between`,
                    }}
                    hideSeparator
                    containerStyle={{
                      width: `unset`,
                    }}
                  />
                ) : null}
              </Grid>
            </Grid>

            <VSpacer large />

            <Grid container item justifyContent="flex-end" direction="row" wrap="nowrap">
              <ButtonGroup disableElevation disableRipple disableFocusRipple variant="contained">
                <MButton
                  disabled={isLoading}
                  color="secondary"
                  style={{
                    backgroundColor: !isMapView() ? RoutalPalette.secondary40 : RoutalPalette.secondary00,
                    borderBottomLeftRadius: `5px`,
                    borderTopLeftRadius: `5px`,
                  }}
                  onClick={() => {
                    navigate(routing.PROJECT_SETTINGS.CLIENTS.LIST.replace(`:projectId`, activeProject?.id ?? ``));
                  }}
                >
                  <TocIcon style={{ fontSize: `20px` }} />
                </MButton>
                <MButton
                  disabled={isLoading}
                  color="secondary"
                  style={{
                    backgroundColor: isMapView() ? RoutalPalette.secondary40 : RoutalPalette.secondary00,
                    borderBottomRightRadius: `5px`,
                    borderTopRightRadius: `5px`,
                  }}
                  onClick={() => {
                    navigate(routing.PROJECT_SETTINGS.CLIENTS.MAP.replace(`:projectId`, activeProject?.id ?? ``));
                  }}
                >
                  <MapIcon style={{ fontSize: `20px` }} />
                </MButton>
              </ButtonGroup>
            </Grid>
          </Grid>

          <VSpacer medium />

          <Routes>
            <Route
              index
              element={
                <Navigate to={routing.PROJECT_SETTINGS.CLIENTS.LIST.replace(`:projectId`, activeProject?.id ?? ``)} />
              }
            />
            <Route
              path="/list"
              element={
                <ProjectAssetsClientsList
                  searchParameters={searchParameters}
                  sortClientsInfo={sortClientsInfo}
                  setSortClientsInfo={setSortClientsInfo}
                  setSearchParameters={setSearchParameters}
                />
              }
            />
            <Route path="/map" element={<ProjectAssetsClientsMap />} />
            <Route
              path="*"
              element={
                <Navigate to={routing.PROJECT_SETTINGS.CLIENTS.LIST.replace(`:projectId`, activeProject?.id ?? ``)} />
              }
            />
          </Routes>
        </Grid>
      </Grid>

      {detailType === `client` ? <ClientRightPanel /> : null}
    </div>
  );
});
